import { parseCsvBooleanStrict, parseOptionalInt, parseOptionalNumber } from '../../domain/csv/csvParseHelpers';
import { resolveMasterFromRecordFields, type MasterMatchCandidate } from '../../domain/csv/masterMatching';
import { createSupplier, updateSupplier } from '../suppliersRepo';
import type { SupplierRecord } from '../suppliersRepo';
import {
  loadSupplierCandidates,
  loadSupplierMap,
  previewRow,
  supplierCsvWouldChange,
  type CsvPreviewRowOutcome,
  type ParsedRow,
} from './catalogCsvImportHelpers';

export type SupplierCsvPreviewContext = {
  supplierMasterCandidates: MasterMatchCandidate[];
  supplierFullMap: Map<string, SupplierRecord>;
};

export async function loadSupplierCsvPreviewContext(): Promise<SupplierCsvPreviewContext> {
  const supplierMasterCandidates = await loadSupplierCandidates();
  const supplierFullMap = await loadSupplierMap();
  return { supplierMasterCandidates, supplierFullMap };
}

export function previewSupplierCsvRow(
  rowNumber: number,
  record: ParsedRow,
  ctx: SupplierCsvPreviewContext
): CsvPreviewRowOutcome {
  const nombre = record.nombre?.trim();
  if (!nombre) {
    return {
      row: previewRow(rowNumber, 'error', 'validar', 'Sin nombre', 'Ingresá nombre.'),
      okCount: 0,
      skipCount: 0,
      errorCount: 1,
      newProductCount: 0,
    };
  }
  const match = resolveMasterFromRecordFields(ctx.supplierMasterCandidates, {
    id: record.proveedor_id,
    label: nombre,
  });
  if (match.error) {
    return {
      row: previewRow(rowNumber, 'error', 'validar', nombre, match.error),
      okCount: 0,
      skipCount: 0,
      errorCount: 1,
      newProductCount: 0,
    };
  }
  const activoCell = parseCsvBooleanStrict(record.activo);
  if (activoCell.error) {
    return {
      row: previewRow(rowNumber, 'error', 'validar', nombre, activoCell.error),
      okCount: 0,
      skipCount: 0,
      errorCount: 1,
      newProductCount: 0,
    };
  }
  if (match.id) {
    const existing = ctx.supplierFullMap.get(match.id);
    if (existing && !supplierCsvWouldChange(existing, record)) {
      return {
        row: previewRow(rowNumber, 'skip', 'omitir', nombre),
        okCount: 0,
        skipCount: 1,
        errorCount: 0,
        newProductCount: 0,
      };
    }
  }
  return {
    row: previewRow(rowNumber, 'ok', match.id ? 'actualizar' : 'crear', nombre),
    okCount: 1,
    skipCount: 0,
    errorCount: 0,
    newProductCount: 0,
  };
}

export async function commitSupplierRow(
  record: ParsedRow,
  preloadedCandidates?: MasterMatchCandidate[]
): Promise<'created' | 'updated'> {
  const nombre = record.nombre?.trim();
  if (!nombre) throw new Error('Nombre obligatorio.');
  const candidates = preloadedCandidates ?? await loadSupplierCandidates();
  const match = resolveMasterFromRecordFields(candidates, { id: record.proveedor_id, label: nombre });
  if (match.error) throw new Error(match.error);

  const isActiveCell = parseCsvBooleanStrict(record.activo);
  if (isActiveCell.error) throw new Error(isActiveCell.error);
  const leadDays = parseOptionalInt(record.dias_entrega);
  const minOrder = parseOptionalNumber(record.monto_minimo_pedido);

  const payload = {
    name: nombre,
    code: record.codigo?.trim() || null,
    taxId: record.cuit?.trim() || null,
    contactName: record.contacto_nombre?.trim() || null,
    contactEmail: record.contacto_email?.trim() || null,
    contactPhone: record.contacto_telefono?.trim() || null,
    address: record.direccion?.trim() || null,
    city: record.ciudad?.trim() || null,
    state: record.provincia?.trim() || null,
    zip: record.codigo_postal?.trim() || null,
    paymentTerms: record.plazo_pago?.trim() || null,
    leadTimeDays: leadDays ?? null,
    minimumOrderAmount: minOrder ?? null,
    website: record.sitio_web?.trim() || null,
    notes: record.notas?.trim() || null,
    accountNumber: record.numero_cuenta?.trim() || null,
  };

  if (match.id) {
    const existing = (await loadSupplierMap()).get(match.id);
    await updateSupplier({
      id: match.id,
      ...payload,
      isActive: isActiveCell.value ?? existing?.isActive ?? true,
    });
    return 'updated';
  }

  await createSupplier({ ...payload, isActive: isActiveCell.value ?? true });
  return 'created';
}
